import { useEffect } from 'react';
import { UseFormGetValues, UseFormRegister, UseFormUnregister } from 'react-hook-form';

import { ExtendedFieldValueDict, FormField, ValidationFn } from './types';
import { useStringArrayTracking } from './useStringArrayTracking';
import {
  findFormField,
  pluckTruthyFieldKeys,
  requiredOrFalse,
  validateOrUndefined,
} from './util';

const difference = (arr1: string[], arr2: string[]) => {
  const set2 = new Set(arr2);
  return arr1.filter((item) => !set2.has(item));
};

export const useFormHooks = (
  formFields: Array<FormField>,
  register: UseFormRegister<ExtendedFieldValueDict>,
  unregister: UseFormUnregister<ExtendedFieldValueDict>,
  getValues: UseFormGetValues<ExtendedFieldValueDict>,
) => {
  const {
    addStrings: addFields,
    removeStrings: removeFields,
    stringArr: registeredFields,
  } = useStringArrayTracking();

  useEffect(() => {
    const currentFieldKeys = pluckTruthyFieldKeys(formFields);
    const unregisteredFieldKeys = difference(currentFieldKeys, registeredFields);
    addFields(unregisteredFieldKeys);
    unregisteredFieldKeys.forEach((fieldKey) => {
      const currentField = findFormField(fieldKey, formFields);
      if (currentField && 'fieldKey' in currentField) {
        const { fieldKey, required, validation = {} } = currentField;
        const validate = validateOrUndefined(validation) as ValidationFn | undefined;
        register(fieldKey, {
          ...validation,
          required: required ?? requiredOrFalse(validation),
          validate: validate ? (v) => validate(v, getValues()) : undefined,
        });
      }
    });
  }, [formFields, register]);

  useEffect(() => {
    const currentFieldKeys = pluckTruthyFieldKeys(formFields);
    const unusedRegisteredFieldKeys = difference(registeredFields, currentFieldKeys);
    removeFields(unusedRegisteredFieldKeys);
    unusedRegisteredFieldKeys.forEach((fieldKey) => {
      unregister(fieldKey);
    });
  }, [formFields, unregister]);
};
